import type { UseResizableReturn } from 'hooks/useResizable';

import { Colors } from 'styles';
import styled from 'styled-components';

import { useMemo } from 'react';
import { Resizable } from 're-resizable';
import type { FlattenSimpleInterpolation } from 'styled-components';

// @ts-expect-error [EN-7967] - TS2769 - No overload matches this call.
const ResizeHandle = styled.div.attrs({ tabIndex: 0, role: 'button' })`
  display: flex;
  align-items: center;
  width: 100%;
  height: 10px;

  &::before {
    content: '';
    display: block; /* <-- add */
    width: 100%;
    height: 0; /* <-- add (so only the border shows) */
    flex: 1; /* optional, helps in flex contexts */
    transition: border-color 0.2s ease-in-out;

    border-top: ${(props: { topOrBottom: 'top' | 'bottom'; collapsed: boolean }) =>
      props.topOrBottom === 'top'
        ? `2px solid ${props.collapsed ? Colors.gray5 : Colors.gray4}`
        : 'none'};

    border-bottom: ${(props: { topOrBottom: 'top' | 'bottom'; collapsed: boolean }) =>
      props.topOrBottom === 'bottom'
        ? `2px solid ${props.collapsed ? Colors.gray5 : Colors.gray4}`
        : 'none'};
  }

  &:hover,
  &:active {
    &::before {
      border-top-color: ${(props: {
        showCollapseWarning: boolean;
        topOrBottom: 'top' | 'bottom';
      }) =>
        props.topOrBottom === 'top'
          ? props.showCollapseWarning
            ? Colors.red4
            : Colors.gray5
          : undefined};

      border-bottom-color: ${(props: {
        showCollapseWarning: boolean;
        topOrBottom: 'top' | 'bottom';
      }) =>
        props.topOrBottom === 'bottom'
          ? props.showCollapseWarning
            ? Colors.red4
            : Colors.gray5
          : undefined};
    }
  }

  &:focus {
    outline: 0;
  }
`;

export type ResizableSectionProps = {
  className?: string;
  currentMaxHeight: number;
  minSectionHeight: number | string;
  useResizableProps: UseResizableReturn;
  expanded: boolean;
  heading: Nullable<React.ReactNode>;
  children: React.ReactNode;
  topOrBottom?: 'top' | 'bottom';
  resizableStyles?: FlattenSimpleInterpolation;
  ['data-testid']?: string;
  disableResize?: boolean;
};

export const ResizableSection = ({
  children,
  className,
  currentMaxHeight,
  minSectionHeight,
  useResizableProps,
  expanded,
  heading,
  topOrBottom = 'bottom',
  resizableStyles,
  disableResize,
  ...rest
}: ResizableSectionProps): React.ReactElement => {
  const {
    componentSize,
    openComponent,
    isResizing,
    resizableCallbacks,
    resizeListener,
    showCollapseWarning,
  } = useResizableProps;

  const size = useMemo(() => ({ width: '100%', height: componentSize }), [componentSize]);

  const handleComponent = useMemo(
    () => (
      <ResizeHandle
        // @ts-expect-error [EN-7967] - TS2769 - No overload matches this call.
        showCollapseWarning={showCollapseWarning}
        collapsed={!openComponent && !isResizing}
        topOrBottom={topOrBottom}
      />
    ),
    [isResizing, openComponent, showCollapseWarning, topOrBottom]
  );

  return (
    <Resizable
      size={disableResize ? { width: '100%', height: 'auto' } : size}
      {...resizableCallbacks}
      maxHeight={disableResize ? undefined : currentMaxHeight}
      minHeight={disableResize ? undefined : minSectionHeight}
      enable={{ [topOrBottom]: !disableResize }}
      handleComponent={disableResize ? {} : { [topOrBottom]: handleComponent }}
      style={disableResize ? { flex: 1 } : undefined}
      css={`
        display: flex;
        flex-direction: column;
        transition: height ${isResizing === false ? 0.2 : 0}s ease-in-out;
        ${resizableStyles}
      `}
    >
      {resizeListener}
      {heading}
      {expanded && children}
    </Resizable>
  );
};
